import { api, apiUrl, resolveTenantSlug, tokens } from './api';

/**
 * One report this caller may download, exactly as the server offers it.
 *
 * ⚠ The catalogue is filtered BY ROLE ON THE SERVER — an admin gets seven entries, a principal
 * three, a teacher one. Render what comes back and nothing else. A hardcoded list here would drift
 * from the policies in ExportEndpoints.cs the first time either side changes, and the failure is
 * ugly: a button labelled with the name of data the person is not allowed to have.
 */
export interface ExportItem {
  kind: string;
  label: string;
  /** The export covers a date range; send `from` and `to`. */
  needsDates: boolean;
  /** ⚠ This caller must pick a class first — a teacher may only pull a class they actually teach. */
  needsClass: boolean;
  /**
   * Which optional filters this report understands — "class", "department", "status",
   * "staffStatus", "exam", "certificateType".
   *
   * ⚠ NAMES FROM THE SERVER, RENDERED BLIND. This screen does not know what any of them mean; it
   * looks each key up in the options bundle and draws a dropdown. That is deliberate — a filter the
   * server does not declare cannot be shown, so a control that reaches no query cannot exist, and
   * the way to add one is to make the export honour it first.
   */
  filters: string[];
}

/** One choice in one filter. `count` is a roster size where there is one. */
export interface FilterChoice { value: string; label: string; count: number | null }

/**
 * Every filter list this caller may use, in one call, already narrowed by role on the server.
 *
 * ⚠ Keys match the strings in ExportItem.filters exactly. An empty list means the filter is not
 * offered — a school with no exams gets no exam dropdown rather than an empty one.
 */
export interface ExportOptions {
  class: FilterChoice[];
  department: FilterChoice[];
  exam: FilterChoice[];
  status: FilterChoice[];
  staffStatus: FilterChoice[];
  certificateType: FilterChoice[];
}

/** A class this teacher teaches, from the timetable — the only classes they may export. */
export interface TeacherClass { classId: string; label: string; studentCount: number; }

/** A class or a department to narrow a report to. Already filtered to what the caller may see. */
export interface RegisterGroup { id: string; label: string; people: number }

export interface RegisterDay {
  day: number; date: string; isSunday: boolean; isHoliday: boolean;
  holidayName: string | null; wasTaken: boolean;
}

export interface RegisterRow {
  personId: string; serial: string; code: string; name: string; group: string | null;
  /** ⚠ Already letters — P, A, L, CL, ½, H, — or blank. Never map these to anything. */
  cells: string[];
  present: number; absent: number; leave: number; halfDays: number;
  marked: number; percent: number;
}

export interface RegisterReport {
  title: string; scope: string; year: number; month: number; monthName: string;
  workingDays: number; daysTaken: number; averagePercent: number; note: string | null;
  days: RegisterDay[];
  rows: RegisterRow[];
  legend: { letter: string; means: string }[];
}

export type RegisterScope = 'students' | 'staff';

export const reportsApi = {
  catalogue: () => api.get<ExportItem[]>('/api/exports'),
  options: () => api.get<ExportOptions>('/api/exports/options'),
  myClasses: () => api.get<TeacherClass[]>('/api/classroom/my-classes'),

  /** ⚠ Whatever this returns IS the permission, made visible. Do not filter it again here. */
  registerGroups: (scope: RegisterScope) =>
    api.get<RegisterGroup[]>(`/api/exports/register/groups?scope=${scope}`),

  register: (scope: RegisterScope, year: number, month: number, groupId?: string) => {
    const q = new URLSearchParams({ scope, year: String(year), month: String(month) });
    if (groupId) q.set(scope === 'staff' ? 'departmentId' : 'academicClassId', groupId);
    return api.get<RegisterReport>(`/api/exports/register?${q}`);
  },
};

export interface ExportParams {
  from?: string; to?: string;
  academicClassId?: string; departmentId?: string;
  status?: string; examName?: string; certificateType?: string;
}

/**
 * Pull the filename the server chose out of Content-Disposition.
 *
 * Worth the trouble because the server puts the date range — and, for a teacher, the class — into
 * the name. Three files all called attendance.csv have to be opened one at a time to tell apart.
 */
function fileNameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback;
  // RFC 5987 form first: it is the one that survives a non-ASCII name, and when both are present
  // it is the authoritative one.
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through to the plain form */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : fallback;
}

/**
 * Download one export and hand the browser the file. Resolves with the filename that was saved.
 *
 * ⚠ A fetch and a blob rather than an `<a href>`. These routes require a bearer token; the browser
 * issues a plain link's request on its own, without the header, and the school gets a 401 page
 * instead of their report.
 */
export async function downloadExport(kind: string, params: ExportParams): Promise<string> {
  // ⚠ Empty means "no filter" and must not be sent. `?status=` reaches the server as an empty
  // string, not as absent — and an empty string is not a status, so the export would have to decide
  // what to do with it. Not sending it is the only reading with one meaning.
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) q.set(key, value);
  }
  const query = q.toString();

  const slug = resolveTenantSlug();
  const res = await fetch(apiUrl(`/api/exports/${encodeURIComponent(kind)}${query ? `?${query}` : ''}`), {
    headers: {
      ...(slug ? { 'X-Tenant': slug } : {}),
      ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
    },
  });

  if (!res.ok) {
    // ⚠ A 400 carries `{ message }` written for the person reading it ("You can only download
    // attendance for a class you teach"). Show it verbatim — anything generic throws away the one
    // sentence that says what to do next.
    const text = await res.text().catch(() => '');
    let message = `The download failed (${res.status}).`;
    try {
      const body = text ? (JSON.parse(text) as { message?: string }) : null;
      if (body?.message) message = body.message;
    } catch { /* not JSON — keep the generic line */ }
    throw new Error(message);
  }

  const name = fileNameFrom(res.headers.get('Content-Disposition'), `${kind.toLowerCase()}.csv`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
  } finally {
    // ⚠ Always revoke. An object URL that is never released pins the whole CSV in memory for the
    // life of the tab, and these files are the big ones.
    URL.revokeObjectURL(url);
  }
  return name;
}
